import { useState } from "react";
import {
	FileText,
	Download,
	Eye,
	X,
	Loader2,
} from "lucide-react";
import { useApp } from "../components/AppContext";
import { MainLayout } from "../components/MainLayout";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { useMyPayslips } from "../api/payroll/useMyPayslips";
import { useDownloadPayslip } from "../api/payroll/useDownloadPayslip";

export function PayrollInfo() {
	const { navigateDirectly } = useApp();
	const { user, loading: userLoading } = useCurrentUser();
	const { payslips, loading: payslipsLoading, error } = useMyPayslips(user?.extraId);
	const { downloading, downloadPayslip } = useDownloadPayslip();

	const loading = userLoading || payslipsLoading;

	// PDF viewer state
	const [showPdfModal, setShowPdfModal] = useState(false);
	const [selectedPayslip, setSelectedPayslip] = useState<any>(null);
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);
	const [loadingPdf, setLoadingPdf] = useState(false);

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("de-DE", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	};

	const handleViewPdf = async (payslip: any) => {
		try {
			const token = localStorage.getItem("accessToken");
			if (!token) {
				throw new Error("Keine Authentifizierung verfügbar");
			}

			setSelectedPayslip(payslip);
			setLoadingPdf(true);
			setShowPdfModal(true);

			const response = await fetch(
				`${import.meta.env.VITE_API_URL || "http://localhost:3001/api"}/payroll/user/${payslip.extra_id}/download/${payslip.payroll_id}`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (!response.ok) {
				throw new Error(`Fehler beim Laden der PDF: ${response.statusText}`);
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			setPdfUrl(url);
		} catch (error: any) {
			console.error("Error loading PDF:", error);
			setShowPdfModal(false);
		} finally {
			setLoadingPdf(false);
		}
	};

	const handleDownload = async (extraId: number, payrollId: number) => {
		try {
			await downloadPayslip(extraId, payrollId);
		} catch (error) {
			console.error("Error downloading payslip:", error);
		}
	};

	const handleClosePdfModal = () => {
		setShowPdfModal(false);
		setSelectedPayslip(null);
		if (pdfUrl) {
			window.URL.revokeObjectURL(pdfUrl);
			setPdfUrl(null);
		}
	};

	// Loading state
	if (loading) {
		return (
			<MainLayout showBack={true} onBack={() => navigateDirectly("dashboard")}>
				<div className="flex items-center justify-center py-8">
					<div className="text-center">
						<div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
						<p className="text-[var(--ucm-text-medium)]">Lädt...</p>
					</div>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout
			showBack={true}
			onBack={() => navigateDirectly("dashboard")}
		>
			<div className="px-6">
				<div className="mb-6">
					<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-2">
						Lohnabrechnungen
					</h1>
					<p className="text-[var(--ucm-text-medium)]">
						Ihre Lohnabrechnungsdokumente
					</p>
				</div>

				{error ? (
					<div className="bg-red-50 border border-red-200 rounded-2xl p-5">
						<div className="flex items-center space-x-3">
							<FileText size={20} className="text-red-500" />
							<div>
								<h3 className="font-semibold text-red-700">
									Fehler beim Laden der Lohnabrechnungen
								</h3>
								<p className="text-sm text-red-600">{error}</p>
							</div>
						</div>
					</div>
				) : payslips.length === 0 ? (
					<div className="bg-[var(--ucm-light-blue)] rounded-2xl p-8 text-center mt-8">
						<div className="max-w-md mx-auto">
							<div className="mb-4">
								<svg
									className="w-16 h-16 mx-auto text-[var(--ucm-primary-teal)]"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							</div>
							<h2 className="text-xl font-semibold text-[var(--ucm-text-dark)] mb-3">
								Noch keine Dokumente verfügbar
							</h2>
							<p className="text-[var(--ucm-text-medium)]">
								Ihre Lohnabrechnungsdokumente werden hier verfügbar sein, nachdem
								Sie Ihr Profil vervollständigt und erfolgreich am Probebetrieb
								teilgenommen haben.
							</p>
						</div>
					</div>
				) : (
					<div className="space-y-4">
						{payslips.map((payslip) => (
							<div
								key={payslip.payroll_id}
								className="bg-white rounded-2xl p-5 shadow-[0_4px_16px_rgba(0,0,0,0.08)]"
							>
								<div className="flex items-center space-x-3">
									<div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
										<FileText size={20} className="text-blue-600" />
									</div>
									<div className="flex-1 min-w-0">
										<h3 className="font-semibold text-[var(--ucm-text-dark)]">
											Lohnabrechnung
										</h3>
									</div>
								</div>
								<div className="flex items-center space-x-2 mt-3">
									<button
										onClick={() => handleViewPdf(payslip)}
										className="h-10 flex-1 bg-blue-600 text-white font-medium rounded-full hover:bg-blue-700 transition-colors duration-200 text-sm flex items-center justify-center space-x-2"
									>
										<Eye size={16} />
										<span>Anzeigen</span>
									</button>
									<button
										onClick={() => handleDownload(payslip.extra_id, payslip.payroll_id)}
										disabled={downloading}
										className="h-10 w-10 bg-gray-100 text-gray-700 font-medium rounded-full hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
									>
										{downloading ? (
											<div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
										) : (
											<Download size={16} />
										)}
									</button>
								</div>
							</div>
						))}
					</div>
				)}

				{/* PDF Viewer Modal */}
				{showPdfModal && (
					<div
						className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4"
						onClick={handleClosePdfModal}
					>
						<div
							className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col"
							onClick={(e) => e.stopPropagation()}
						>
							{/* Modal Header */}
							<div className="flex items-center justify-between p-4 sm:p-6 border-b">
								<div className="flex items-center space-x-3">
									<div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
										<FileText size={16} className="text-blue-600" />
									</div>
									<h3 className="text-base sm:text-lg font-semibold text-[var(--ucm-text-dark)]">
										Lohnabrechnung
									</h3>
								</div>
								<button
									onClick={handleClosePdfModal}
									className="p-2 hover:bg-gray-100 rounded-full transition-colors"
								>
									<X size={20} className="text-gray-500" />
								</button>
							</div>

							{/* Modal Body */}
							<div className="flex-1 p-3 sm:p-6 overflow-hidden">
								{loadingPdf ? (
									<div className="flex flex-col items-center justify-center h-60 sm:h-96">
										<Loader2
											size={40}
											className="animate-spin text-blue-600 mb-4"
										/>
										<p className="text-[var(--ucm-text-medium)]">
											PDF wird geladen...
										</p>
									</div>
								) : pdfUrl ? (
									<iframe
										src={`${pdfUrl}#toolbar=0`}
										className="w-full h-[60vh] sm:h-96 border rounded-lg"
										title="Lohnabrechnung"
									/>
								) : (
									<div className="flex flex-col items-center justify-center h-60 sm:h-96">
										<FileText size={40} className="text-gray-400 mb-4" />
										<p className="text-[var(--ucm-text-medium)]">
											Fehler beim Laden der PDF
										</p>
									</div>
								)}
							</div>

							{/* Modal Footer */}
							{selectedPayslip && (
								<div className="p-4 sm:p-6 border-t bg-gray-50 rounded-b-2xl">
									<button
										onClick={() => handleDownload(selectedPayslip.extra_id, selectedPayslip.payroll_id)}
										disabled={downloading}
										className="h-10 w-full sm:w-auto sm:float-right px-4 bg-blue-600 text-white font-medium rounded-full hover:bg-blue-700 transition-colors duration-200 text-sm flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
									>
										{downloading ? (
											<>
												<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
												<span>Lädt...</span>
											</>
										) : (
											<>
												<Download size={16} />
												<span>Herunterladen</span>
											</>
										)}
									</button>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</MainLayout>
	);
}
